import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CommercialSummary {
  lead_id: string;
  workspace_id: string;
  open_deals_count: number;
  open_deals_amount: number;
  open_weighted_amount: number;
  won_deals_amount: number;
  lost_deals_count: number;
  has_open_deal: boolean;
  primary_open_deal_id: string | null;
  primary_open_deal_title: string | null;
  primary_open_deal_stage_name: string | null;
  primary_open_deal_amount: number | null;
  primary_product_name: string | null;
  next_expected_close_date: string | null;
  last_deal_created_at: string | null;
}

export interface CommercialDeal {
  id: string;
  title: string;
  amount: number;
  weighted_amount: number;
  status: string;
  stage_id: string;
  stage_name: string;
  stage_color: string;
  probability: number;
  expected_close_date: string | null;
  created_at: string;
  notes: string | null;
}

export interface CommercialInterest {
  id: string;
  product_id: string | null;
  product_name_snapshot: string;
  interest_level: string;
  is_primary: boolean;
  quantity_estimate: number | null;
  budget_estimate: number | null;
  notes: string | null;
  created_at: string;
}

export function useLeadCommercialProfile(leadId: string | null) {
  const queryKey = ["lead-commercial-profile", leadId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!leadId) return { summary: null, deals: [], interests: [] };
      const { data, error } = await supabase.rpc("get_lead_commercial_profile", {
        p_lead_id: leadId,
      });
      if (error) throw error;
      const result = data as any;
      return {
        summary: (result?.summary ?? null) as CommercialSummary | null,
        deals: (result?.deals ?? []) as CommercialDeal[],
        interests: (result?.interests ?? []) as CommercialInterest[],
      };
    },
    enabled: !!leadId,
  });

  return {
    summary: query.data?.summary ?? null,
    deals: query.data?.deals ?? [],
    interests: query.data?.interests ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
